/**
 * Query parameters known to carry no PHI: filter selections, pagination, view state and
 * opaque record ids. Everything else is treated as possibly identifying.
 *
 * An allow-list, not a deny-list, on purpose. Several pages put free text in the URL — the
 * Referrals search (`search`), the Documents patient-name search (`searchPatientName`),
 * React Admin's `filter` JSON — and a deny-list is only as good as the last page someone
 * remembered to add to it. A new parameter stays out of Mixpanel and Sentry until it is added
 * here deliberately.
 */
export const PHI_SAFE_QUERY_PARAMS: ReadonlySet<string> = new Set([
  // Pagination and view state
  'page',
  'limit',
  'tab',
  'view',
  // Referrals list filters
  'clinicIds',
  'reviewStates',
  'systemSources',
  'includeDiscarded',
  // Documents list filters
  'processingStatuses',
  'processingSteps',
  'sourceTypes',
  'documentTypes',
  'receivingClinicIds',
  'detectedClinicIds',
  'hasReferrals',
  'requiresReview',
  'isProcessed',
  'searchDocumentId',
  // Opaque record ids
  'claimId',
  'processId',
  'voicemailId',
]);

/**
 * A location's query rebuilt from its PHI-safe parameters only, for analytics. Anything not on
 * the allow-list is dropped, key and value.
 */
export const analyticsSafeSearch = (search: string | undefined): string => {
  const kept = new URLSearchParams();
  new URLSearchParams(search ?? '').forEach((value, key) => {
    if (PHI_SAFE_QUERY_PARAMS.has(key)) {
      kept.append(key, value);
    }
  });
  const query = kept.toString();
  return query ? `?${query}` : '';
};

/**
 * A full URL with every query reduced to its PHI-safe parameters, for analytics SDKs that
 * record the page URL on their own (Mixpanel's `$current_url`, `$referrer`). The app uses hash
 * routing, so the route's query lives after the `#` (`#/documents/library?searchPatientName=…`)
 * and is cleaned as well as the one before it.
 */
export const analyticsSafeUrl = (href: string): string => {
  const withSafeQuery = (part: string): string => {
    const queryStart = part.indexOf('?');
    return queryStart === -1
      ? part
      : `${part.slice(0, queryStart)}${analyticsSafeSearch(part.slice(queryStart))}`;
  };
  const hashStart = href.indexOf('#');
  if (hashStart === -1) {
    return withSafeQuery(href);
  }
  return `${withSafeQuery(href.slice(0, hashStart))}${withSafeQuery(href.slice(hashStart))}`;
};

/**
 * `?key=value` / `&key=value` pairs anywhere in a string: a URL, or a message quoting one.
 *
 * The value runs to the next `&`, `#` or the end of the string, spaces included, so an unencoded
 * `?search=Jane Doe` in a free-form message is redacted whole. That over-redacts any text that
 * follows a URL in a message, which is the safe direction. The key excludes `?` so a long run of
 * `?` cannot make the match quadratic.
 */
const QUERY_PAIR = /([?&])([^=&#?\s]+)=([^&#]*)/g;

/**
 * Redacts the value of every query parameter not on the allow-list, anywhere in a string.
 * The key is kept so an error report still says which parameter was there.
 */
export const redactQueryParams = (value: string): string =>
  value.replace(QUERY_PAIR, (pair, separator: string, key: string) => {
    let name = key;
    try {
      name = decodeURIComponent(key);
    } catch {
      // A malformed escape is not an allow-listed name either way.
    }
    return PHI_SAFE_QUERY_PARAMS.has(name) ? pair : `${separator}${key}=[REDACTED]`;
  });
